import {Alert, Box, CircularProgress, Grid, Snackbar, Typography} from '@mui/material';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {t as i18nextT} from 'i18next';
import axios from 'axios';
import {flushSync} from 'react-dom';

import ChevronRight from '@mui/icons-material/ChevronRight';
import TrendingUp from '@mui/icons-material/TrendingUp';
import {LanguageSelector} from './LanguageSelector';
import {CardField, GoButton} from './CardControls';
import defaultBackground from './assets/default-bg.jpg';
import {createLoginLaunch, prefersReducedMotion} from './LoginLaunch';
import {getApiBaseUrl, getAppUrl, getStartUrl, isCustomerControlledDomain} from './UrlUtil';

const axiosClient = axios.create({
	baseURL: getApiBaseUrl(),
	withCredentials: true,
	headers: {'Content-Type': 'application/json'},
});

// Pre-fetch fallback. Used only for the brief moment before /api/branding
// resolves. Kept intentionally bland (default partihandel grey) so users on
// a tenant subdomain don't see another tenant's colors flash. The real
// per-tenant config lives in the database, fetched via the loadbalancer.
// On a customer-controlled domain (e.g. id.lotusfoodservice.se) the
// partihandel.se logo must never flash before that per-tenant logo loads.
const FALLBACK_BRANDING = {
	logoUrl: isCustomerControlledDomain ? null : 'https://storage.googleapis.com/pifa.se/partihandel.se_row.png',
	logoStyle: {},
	primaryColor: '#365F62',
	primaryColorHover: '#2a4b50',
	buttonTextColor: '#ffffff',
	// White/near-black text: the form fields below (TextField, labels, icons)
	// are unstyled MUI defaults designed for a light card — they were never
	// made dark-card-aware. A dark default here would make them illegible.
	cardBackground: '#ffffff',
	cardTextColor: '#1a1a1a',
	rightPanelGradient: 'linear-gradient(135deg, #112427, #365f62)',
	leftBackgroundImage: null,
	fullScreenBackgroundImage: false,
	title: `partihandel.se • ${i18nextT('login.login', {defaultValue: 'Logga in'})}`,
};

const TENANT_GROUP_KEYS = ['active', 'trading', 'inactive'];
const TENANT_GROUP_META = {
	active: {labelKey: 'login.tenantGroupActive', color: '#43a047'},
	trading: {labelKey: 'login.tenantGroupTrading', color: '#9e9e9e'},
	inactive: {labelKey: 'login.tenantGroupInactive', color: '#ef5350'},
};
const getTenantGroupKey = c => (c.tradingOnly ? 'trading' : c.isActive === false ? 'inactive' : 'active');

// Leading row indicator: green dot / red dot / trending icon, matching
// the Settings → User company picker.
const TenantRowIndicator = ({groupKey}) => {
	if (groupKey === 'trading') return <TrendingUp fontSize="small" sx={{color: 'inherit', opacity: 0.7}} />;
	const color = groupKey === 'inactive' ? '#ef5350' : '#43a047';
	return <Box sx={{width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0}} />;
};

// The login card's logo. With branding.logoTrim (measured by pifa-api when the
// branding is published) it is cropped to the artwork box, so padding baked
// into the file doesn't shrink it; without, the image is shown as it is.
const LoginLogo = ({url, trim, style, alt}) => {
	if (!trim) return <img src={url} alt={alt} className="login-logo-img" style={style} />;
	// background-position as a percentage aligns that point of the image with
	// the same point of the box, so x / (1 - w) puts the box's left edge at x.
	const pos = (offset, size) => (size < 1 ? (offset / (1 - size)) * 100 : 0);
	return (
		<div
			role="img"
			aria-label={alt}
			className="login-logo-trim"
			style={{
				'--logo-aspect': trim.aspect,
				backgroundImage: `url("${url}")`,
				backgroundSize: `${100 / trim.w}% ${100 / trim.h}%`,
				backgroundPosition: `${pos(trim.x, trim.w)}% ${pos(trim.y, trim.h)}%`,
			}}
		/>
	);
};

function LoginApp() {
	const [loading, setLoading] = useState(false);
	const {t} = useTranslation();
	const [updateMessage, setUpdateMessage] = useState('');
	const [updateType, setUpdateType] = useState('info');
	const [snackbarOpen, setSnackbarOpen] = useState(false);

	const [showPassword, setShowPassword] = useState(false);
	const handleClickShowPassword = () => setShowPassword(!showPassword);

	const [failedAttempts, setFailedAttempts] = useState(0);

	const [showForgotPassword, setShowForgotPassword] = useState(false);
	const [forgotEmail, setForgotEmail] = useState('');
	const [forgotLoading, setForgotLoading] = useState(false);
	const [forgotSent, setForgotSent] = useState(false);

	// Multi-company picker: when /auth/login returns requiresCompanySelection
	// (user belongs to >1 company), we stash the short-lived selectionToken
	// and the list of companies, then render a picker instead of the form.
	// Bootstrap selectionToken from the URL (?st=...) on first render so that
	// the standalone tenant-select flow from Settings → User lands directly on
	// the picker. The URL param is consumed once on mount; useEffect cleans
	// the address bar afterwards.
	const initialSelectionToken = useMemo(() => {
		try {
			return new URLSearchParams(window.location.search).get('st') || null;
		} catch {
			return null;
		}
	}, []);
	const [selectionToken, setSelectionToken] = useState(initialSelectionToken);
	const [selectableCompanies, setSelectableCompanies] = useState([]);
	const [companySearch, setCompanySearch] = useState('');
	const searchRef = useRef(null);
	// The launch animation (LoginLaunch.js) and the elements it moves.
	const launchCanvasRef = useRef(null);
	const loginRootRef = useRef(null);
	const loginCardRef = useRef(null);
	const loginButtonRef = useRef(null);
	const rowRefs = useRef([]);
	// Mirror of selectionToken for use inside the fetch effect's .catch —
	// lets us suppress the login.selectionExpired snackbar when the user has
	// already clicked "back to login" while the fetch was in flight.
	const selectionTokenRef = useRef(selectionToken);
	useEffect(() => {
		selectionTokenRef.current = selectionToken;
	}, [selectionToken]);

	// While the browser is navigating to the selected company's domain we
	// swap the picker for a clean transition view so the user sees the
	// company name + spinner instead of the bare picker or a snackbar flash.
	const [redirectingTo, setRedirectingTo] = useState(null);

	// Watchdog: if window.location.href = redirectUrl never produces a
	// browser navigation (invalid URL, blocked by CSP, host unreachable),
	// we'd otherwise leave the user on the "Loggar in i..." spinner forever.
	// After 10s, revert to the form and surface a recoverable error.
	useEffect(() => {
		if (!redirectingTo) return;
		const watchdog = setTimeout(() => {
			setRedirectingTo(null);
			setLoading(false);
			setData(d => ({...d, isSubmitting: false, errorMessage: null}));
			setUpdateMessage(t('login.redirectFailed'));
			setUpdateType('error');
			setSnackbarOpen(true);
		}, 10000);
		return () => clearTimeout(watchdog);
	}, [redirectingTo]);

	// Alphabetical (Swedish locale) and filtered by free-text search. The
	// search box only shows for >10 companies — for shorter lists the
	// alphabetical buttons are quick enough.
	const SEARCH_THRESHOLD = 10;
	const filteredCompanies = useMemo(() => {
		const sorted = [...selectableCompanies].sort((a, b) => (a.companyName || '').localeCompare(b.companyName || '', 'sv'));
		const q = companySearch.trim().toLowerCase();
		if (!q) return sorted;
		return sorted.filter(c => (c.companyName || '').toLowerCase().includes(q));
	}, [selectableCompanies, companySearch]);

	// Group filtered companies by active / trading / inactive. Headers only
	// render when more than one group is represented — a restaurant owner with
	// 12 trading-only venues doesn't need a "Trading only" banner above them.
	const groupedCompanies = useMemo(() => {
		const byGroup = new Map();
		for (const key of TENANT_GROUP_KEYS) byGroup.set(key, []);
		for (const c of filteredCompanies) byGroup.get(getTenantGroupKey(c)).push(c);
		const nonEmpty = TENANT_GROUP_KEYS.map(key => ({key, companies: byGroup.get(key)})).filter(g => g.companies.length > 0);
		return {groups: nonEmpty, showHeaders: nonEmpty.length > 1};
	}, [filteredCompanies]);

	// Without a search box (short list), start keyboard focus on the first company.
	useEffect(() => {
		if (!selectionToken || selectableCompanies.length === 0 || selectableCompanies.length > SEARCH_THRESHOLD) return;
		rowRefs.current[0]?.focus();
	}, [selectionToken, selectableCompanies]);

	const [branding, setBranding] = useState(FALLBACK_BRANDING);

	useEffect(() => {
		// Branding is fetched per-host from the loadbalancer's /api/branding
		// endpoint, which looks up the company by domain in the database.
		fetch('/api/branding')
			.then(res => (res.ok ? res.json() : null))
			.then(data => data && setBranding(data))
			.catch(err => console.warn('Failed to load branding:', err));
	}, []);

	const [data, setData] = useState({
		email: '',
		password: '',
		isSubmitting: false,
		errorMessage: null,
	});

	useEffect(() => {
		document.title = branding.title;
	}, [branding.title]);

	// Reflect the picker as its own URL so the page feels like a real route
	// (browser title, shareable URL, back button behaves sensibly).
	useEffect(() => {
		if (selectionToken && window.location.pathname !== '/tenant-select') {
			window.history.pushState({}, '', '/tenant-select');
		} else if (!selectionToken && window.location.pathname === '/tenant-select') {
			window.history.replaceState({}, '', '/');
		}
	}, [selectionToken]);

	// When the picker was reached via a selectionToken from the URL (the
	// "switch company from Settings" flow), the company list isn't in state
	// yet — fetch it from /auth/companies-for-selection. Strip the ?st=…
	// param from the address bar once consumed.
	useEffect(() => {
		if (!initialSelectionToken) return;
		// Clean the URL immediately so the token isn't left in history.
		window.history.replaceState({}, '', '/tenant-select');
		axiosClient
			.post('/auth/companies-for-selection', {selectionToken: initialSelectionToken})
			.then(res => {
				setSelectableCompanies(res.data.companies || []);
			})
			.catch(err => {
				// User clicked "back to login" before the fetch settled —
				// don't pop a stale error toast over the login form.
				if (!selectionTokenRef.current) return;
				const message = err.response?.data?.errorMessage || t('login.selectionExpired');
				setSelectionToken(null);
				setSelectableCompanies([]);
				setUpdateMessage(message);
				setUpdateType('error');
				setSnackbarOpen(true);
			});
	}, [initialSelectionToken]);

	// Browser back/forward navigation should clear the picker, not strand the
	// user on /tenant-select with React state surviving a URL change. Without
	// this, clicking browser-back from the picker visibly does nothing (URL
	// rewinds to /, but the picker keeps rendering off React state).
	useEffect(() => {
		const onPop = () => {
			if (window.location.pathname !== '/tenant-select') {
				setSelectionToken(null);
				setSelectableCompanies([]);
				setCompanySearch('');
			}
		};
		window.addEventListener('popstate', onPop);
		return () => window.removeEventListener('popstate', onPop);
	}, []);

	const emailRef = useRef(null);
	const passwordRef = useRef(null);

	useEffect(() => {
		if (emailRef.current && emailRef.current.value) {
			emailRef.current.focus();
			emailRef.current.blur();
		}
		if (passwordRef.current && passwordRef.current.value) {
			passwordRef.current.focus();
			passwordRef.current.blur();
		}
	}, []);

	const handleInputChange = event => {
		const {name, value} = event.target;
		setData(prevData => ({...prevData, [name]: value, errorMessage: null}));
	};

	const handleCloseSnackbar = () => {
		setSnackbarOpen(false);
	};

	const handleSubmit = async e => {
		e.preventDefault();
		// While the request is pending the button shows a spinner; once the
		// server says yes, LoginLaunch.js lifts the card away and dims the page
		// before the browser moves on. Without it (reduced motion), the card
		// shows the spinner as before.
		let launch = null;
		if (!prefersReducedMotion() && launchCanvasRef.current && loginButtonRef.current) {
			launch = createLoginLaunch({
				canvas: launchCanvasRef.current,
				root: loginRootRef.current,
				card: loginCardRef.current,
				button: loginButtonRef.current,
				brandColor: branding.primaryColor,
			});
			launch.start();
		} else {
			setLoading(true);
		}
		setData({...data, isSubmitting: true, errorMessage: null});

		try {
			const response = await axiosClient.post('/auth/login', {
				email: data.email,
				password: data.password,
			});

			// Server requires the user to pick which company to enter when they
			// belong to more than one (typically restaurant owners with multiple
			// venues). Don't redirect yet — render the picker.
			if (response.data.requiresCompanySelection) {
				const showPicker = () => {
					setSelectionToken(response.data.selectionToken);
					setSelectableCompanies(response.data.companies || []);
					setData({...data, isSubmitting: false, errorMessage: null});
					setLoading(false);
				};
				// The picker is black too, so it takes over from the animation's last frame. It has to be on screen before the canvas goes: stop() hides
				// the canvas and puts the card back, and with the picker only queued that frame shows the login card again. flushSync renders it first.
				if (launch) {
					launch.succeed(() => {
						flushSync(showPicker);
						launch.stop();
					});
				} else {
					showPicker();
				}
				return;
			}

			if (response.data.success) {
				// Keep isSubmitting/loading true so the form doesn't flash back —
				// the redirecting screen takes over until the browser navigates.
				// With the animation, its black last frame stands in for that screen.
				if (!launch) {
					setRedirectingTo({
						_id: null,
						companyName: response.data.companyName || '',
						icon: response.data.icon || '',
					});
				}

				let redirectUrl;
				if (response.data.redirectUrl) {
					redirectUrl = response.data.redirectUrl;
					const intendedPath = sessionStorage.getItem('redirectAfterLogin');
					if (intendedPath && intendedPath !== '/') {
						redirectUrl = `${redirectUrl}${intendedPath}`;
						sessionStorage.removeItem('redirectAfterLogin');
					}
				} else {
					const intendedPath = sessionStorage.getItem('redirectAfterLogin');
					if (intendedPath && intendedPath !== '/') {
						redirectUrl = `${getAppUrl()}${intendedPath}`;
						sessionStorage.removeItem('redirectAfterLogin');
					} else {
						redirectUrl = getAppUrl();
					}
				}

				console.log('Redirecting to:', redirectUrl);
				if (launch) {
					launch.succeed(() => {
						window.location.href = redirectUrl;
						// Same watchdog as the redirecting screen: if the browser never
						// leaves, put the page back and say so instead of staying black.
						setTimeout(() => {
							launch.stop();
							setData(d => ({...d, isSubmitting: false}));
							setUpdateMessage(t('login.redirectFailed'));
							setUpdateType('error');
							setSnackbarOpen(true);
						}, 10000);
					});
				} else {
					window.location.href = redirectUrl;
				}
			}
		} catch (error) {
			console.error('Login error:', error);
			if (launch) launch.fail();
			const serverErrorMessage = error.response?.data?.errorMessage || t('login.loginFailed');

			setFailedAttempts(prev => prev + 1);
			setData({
				...data,
				isSubmitting: false,
				errorMessage: serverErrorMessage,
			});
			setLoading(false);
			// Shown inside the card (data.errorMessage), next to the button that failed, instead of a snackbar at the foot of the page.
		}
	};

	const handleSelectCompany = async companyId => {
		const targetCompany = selectableCompanies.find(c => c._id === companyId);
		setRedirectingTo(targetCompany || {_id: companyId, companyName: ''});
		setData({...data, isSubmitting: true, errorMessage: null});

		try {
			const response = await axiosClient.post('/auth/select-company', {
				selectionToken,
				companyId,
			});

			if (response.data.success) {
				let redirectUrl;
				if (response.data.redirectUrl) {
					redirectUrl = response.data.redirectUrl;
					const intendedPath = sessionStorage.getItem('redirectAfterLogin');
					if (intendedPath && intendedPath !== '/') {
						redirectUrl = `${redirectUrl}${intendedPath}`;
						sessionStorage.removeItem('redirectAfterLogin');
					}
				} else {
					const intendedPath = sessionStorage.getItem('redirectAfterLogin');
					if (intendedPath && intendedPath !== '/') {
						redirectUrl = `${getAppUrl()}${intendedPath}`;
						sessionStorage.removeItem('redirectAfterLogin');
					} else {
						redirectUrl = getAppUrl();
					}
				}

				// Keep the transition screen visible — don't toggle loading
				// or fire snackbars that would flash before navigation starts.
				window.location.href = redirectUrl;
			}
		} catch (error) {
			const serverErrorMessage = error.response?.data?.errorMessage || t('login.selectionFailed');

			// Selection token expired (~2 min TTL) — kick back to login form.
			if (error.response?.status === 401) {
				setSelectionToken(null);
				setSelectableCompanies([]);
				setCompanySearch('');
			}

			// Pull the transition screen down so the user can retry or see the error.
			setRedirectingTo(null);
			setData({...data, isSubmitting: false, errorMessage: serverErrorMessage});
			setLoading(false);
			setUpdateMessage(serverErrorMessage);
			setUpdateType('error');
			setSnackbarOpen(true);
		}
	};

	const handleForgotPasswordSubmit = async e => {
		e.preventDefault();
		setForgotLoading(true);
		try {
			await axiosClient.post('/auth/forgot-password', {email: forgotEmail});
		} catch (_) {
			// Intentionally swallow — never reveal whether account exists
		} finally {
			setForgotLoading(false);
			setForgotSent(true);
		}
	};

	const handleBackToLogin = () => {
		setShowForgotPassword(false);
		setForgotSent(false);
		setForgotEmail('');
	};

	// CSS variables that LoginPage.css picks up via var(--brand-...).
	const brandStyle = {
		'--brand-primary': branding.primaryColor,
		'--brand-primary-hover': branding.primaryColorHover,
		'--brand-button-text': branding.buttonTextColor,
		'--brand-card-bg': branding.cardBackground,
		'--brand-card-text': branding.cardTextColor,
		'--brand-bg': `url("${branding.leftBackgroundImage || defaultBackground}")`,
	};

	const forgotPasswordForm = (
		<Grid container spacing={1}>
			{forgotSent ? (
				<Grid item xs={12}>
					<Alert severity="success" sx={{mb: 2}}>
						<Trans i18nKey="login.forgotPasswordSent" values={{email: forgotEmail}} components={{strong: <strong />}} />
					</Alert>
					<button type="button" className="fld-link" onClick={handleBackToLogin}>
						{t('login.backToLogin')}
					</button>
				</Grid>
			) : (
				<>
					<Grid item xs={12}>
						<Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
							{t('login.forgotPasswordIntro')}
						</Typography>
						<CardField id="forgot-email" name="email" label={t('login.emailAddress')} autoComplete="username" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} autoFocus />
					</Grid>
					<Grid item xs={12}>
						<div className="go-row">
							<GoButton type="button" busy={forgotLoading} disabled={!forgotEmail} onClick={handleForgotPasswordSubmit}>
								{t('login.sendLink')}
							</GoButton>
						</div>
					</Grid>
					<Grid item xs={12} sx={{textAlign: 'center'}}>
						<button type="button" className="fld-link" onClick={handleBackToLogin}>
							{t('login.backToLogin')}
						</button>
					</Grid>
				</>
			)}
		</Grid>
	);

	const loginForm = (
		<Grid container spacing={1}>
			<Grid item xs={12}>
				<CardField
					id="login-username"
					name="email"
					label={t('login.username')}
					autoComplete="username"
					value={data.email}
					onChange={handleInputChange}
					autoFocus
					onKeyDown={e => {
						if (e.key === 'Enter') {
							e.preventDefault();
							passwordRef.current?.focus();
						}
					}}
				/>
			</Grid>
			<Grid item xs={12}>
				<CardField
					id="login-password"
					name="password"
					label={t('login.password')}
					autoComplete="current-password"
					type={showPassword ? 'text' : 'password'}
					inputRef={passwordRef}
					value={data.password}
					onChange={handleInputChange}
					action={
						<button type="button" className="fld-toggle" aria-label={t('login.togglePasswordVisibility')} aria-pressed={showPassword} onMouseDown={e => e.preventDefault()} onClick={handleClickShowPassword}>
							{showPassword ? t('login.hidePassword') : t('login.showPassword')}
						</button>
					}
				/>
			</Grid>
			{data.errorMessage && (
				<Grid item xs={12}>
					<div className="fld-error" role="alert">
						{data.errorMessage}
					</div>
				</Grid>
			)}
			<Grid item xs={12}>
				<div className="go-row">
					<GoButton busy={data.isSubmitting} busyLabel={t('login.loggingIn')} error={Boolean(data.errorMessage)} buttonRef={loginButtonRef}>
						{t('login.login')}
					</GoButton>
				</div>
			</Grid>
			{failedAttempts >= 1 && (
				<Grid item xs={12} sx={{textAlign: 'center'}}>
					<button
						type="button"
						className="fld-link"
						onClick={() => {
							setForgotEmail(data.email);
							setShowForgotPassword(true);
						}}
					>
						{t('login.forgotPassword')}
					</button>
				</Grid>
			)}
		</Grid>
	);

	const cardBody = withTitle => (
		<>
			{withTitle && (
				<Box sx={{display: 'flex', gap: '1rem', alignItems: 'center', mb: 1}}>
					<Typography variant="h5" component="h1" gutterBottom>
						{showForgotPassword ? t('login.forgotPassword') : t('login.welcomeback')}
					</Typography>
				</Box>
			)}
			{loading ? (
				<Grid container>
					<Grid item xs={12} style={{padding: '3rem', textAlign: 'center'}}>
						<CircularProgress style={{margin: '2rem auto'}} />
					</Grid>
				</Grid>
			) : showForgotPassword ? (
				forgotPasswordForm
			) : (
				<form onSubmit={handleSubmit}>{loginForm}</form>
			)}
		</>
	);

	// In-flight transition: between "company clicked" and the browser actually
	// navigating to the target subdomain, show a small Partihandel mark, the
	// target company's own logo prominently in the middle, and a status line.
	// Matches the white picker aesthetic for a smooth visual handoff.
	if (redirectingTo) {
		return (
			<Box
				sx={{
					minHeight: '100vh',
					backgroundColor: '#fff',
					color: '#000',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
					px: 2,
					gap: 2.5,
					position: 'relative',
				}}
			>
				{/* Small Partihandel mark, pinned near the top. Never shown on a customer-controlled domain. */}
				{!isCustomerControlledDomain && <img src="https://storage.googleapis.com/pifa.se/partihandel.se_row.png" alt="Partihandel" style={{height: 28, opacity: 0.7, position: 'absolute', top: 24}} />}

				{redirectingTo.icon ? (
					<img src={redirectingTo.icon} alt={redirectingTo.companyName || ''} style={{maxHeight: 140, maxWidth: 320, objectFit: 'contain'}} />
				) : (
					<Box
						sx={{
							width: 120,
							height: 120,
							borderRadius: '50%',
							backgroundColor: '#f2f2f2',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							fontSize: '2.6rem',
							fontWeight: 600,
							color: '#444',
						}}
					>
						{(redirectingTo.companyName || '?').trim().charAt(0).toUpperCase()}
					</Box>
				)}

				<Typography variant="h6" component="div" sx={{fontWeight: 500, color: '#1a1a1a', textAlign: 'center', mt: 1}}>
					<Trans i18nKey="login.loggingInTo" values={{companyName: redirectingTo.companyName || '…'}} components={{strong: <strong />}} />
				</Typography>
				<CircularProgress size={24} sx={{color: '#1a1a1a'}} />
			</Box>
		);
	}

	// Dedicated /tenant-select page: black background, white logo, compact
	// rows. Fully keyboard driven: arrows move between search and rows,
	// Enter picks, typing on a row jumps back into the search.
	if (selectionToken && !showForgotPassword) {
		const showSearch = selectableCompanies.length > SEARCH_THRESHOLD;
		const flatCompanies = groupedCompanies.groups.flatMap(g => g.companies);
		rowRefs.current.length = flatCompanies.length;
		const focusRow = i => rowRefs.current[i]?.focus();

		const handleSearchKeyDown = e => {
			if (e.key === 'ArrowDown') {
				e.preventDefault();
				focusRow(0);
			} else if (e.key === 'Enter' && flatCompanies.length > 0) {
				e.preventDefault();
				handleSelectCompany(flatCompanies[0]._id);
			}
		};

		const handleRowKeyDown = (e, index, companyId) => {
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				handleSelectCompany(companyId);
			} else if (e.key === 'ArrowDown') {
				e.preventDefault();
				focusRow(Math.min(index + 1, flatCompanies.length - 1));
			} else if (e.key === 'ArrowUp') {
				e.preventDefault();
				if (index === 0) searchRef.current?.focus();
				else focusRow(index - 1);
			} else if (e.key === 'Home') {
				e.preventDefault();
				focusRow(0);
			} else if (e.key === 'End') {
				e.preventDefault();
				focusRow(flatCompanies.length - 1);
			} else if (showSearch && !e.ctrlKey && !e.metaKey && !e.altKey && (e.key.length === 1 || e.key === 'Backspace')) {
				// Moving focus during keydown lets the key land in the search box.
				searchRef.current?.focus();
			}
		};

		let rowIndex = 0;
		return (
			<div className={`tenant-select${isCustomerControlledDomain ? '' : ' has-bg'}`}>
				<Box sx={{position: 'absolute', top: 12, right: 12, zIndex: 10}}>
					<LanguageSelector color={'white'} />
				</Box>

				<div className="tenant-select-inner">
					{showSearch && (
						<input
							ref={searchRef}
							className="tenant-search"
							type="text"
							name="company-search"
							placeholder={t('login.searchCompanyPlaceholder')}
							value={companySearch}
							onChange={e => {
								const value = e.target.value;
								setCompanySearch(value);
							}}
							onKeyDown={handleSearchKeyDown}
							autoFocus
							autoComplete="off"
							autoCapitalize="none"
							autoCorrect="off"
							spellCheck={false}
							data-form-type="other"
							data-lpignore="true"
						/>
					)}

					<div className="tenant-list">
						{flatCompanies.length === 0 ? (
							<div className="tenant-empty">{selectableCompanies.length === 0 ? t('login.noCompaniesLinked') : t('login.noCompaniesMatchSearch')}</div>
						) : (
							groupedCompanies.groups.map(group => {
								const groupMeta = TENANT_GROUP_META[group.key];
								return (
									<React.Fragment key={group.key}>
										{groupedCompanies.showHeaders && (
											<div className="tenant-group-header">
												<span className="tenant-dot" style={{backgroundColor: groupMeta.color}} />
												{t(groupMeta.labelKey)}
											</div>
										)}
										{group.companies.map(company => {
											const index = rowIndex++;
											return (
												<div key={company._id} ref={el => (rowRefs.current[index] = el)} className="tenant-row" role="button" tabIndex={0} onClick={() => handleSelectCompany(company._id)} onKeyDown={e => handleRowKeyDown(e, index, company._id)}>
													<TenantRowIndicator groupKey={group.key} />
													<span className="tenant-row-name">{company.companyName}</span>
													<ChevronRight className="tenant-row-chevron" fontSize="small" />
												</div>
											);
										})}
									</React.Fragment>
								);
							})
						)}
					</div>

					<button
						type="button"
						className="tenant-back"
						onClick={() => {
							setSelectionToken(null);
							setSelectableCompanies([]);
							setCompanySearch('');
						}}
					>
						{t('login.backToLogin')}
					</button>
				</div>

				<Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}>
					<Alert onClose={handleCloseSnackbar} severity={updateType} sx={{width: '100%'}}>
						{updateMessage}
					</Alert>
				</Snackbar>
			</div>
		);
	}

	return (
		<>
			<div className="login-root" style={brandStyle} ref={loginRootRef}>
				<div className="login-lang">
					<LanguageSelector color={'white'} />
				</div>

				<div className="login-card" ref={loginCardRef}>
					{branding.logoUrl && (
						<a href={getStartUrl()} className={`login-logo${branding.logoBackground ? ' band' : ''}`} style={branding.logoBackground ? {background: branding.logoBackground} : undefined}>
							<LoginLogo url={branding.logoUrl} trim={branding.logoTrim} style={branding.logoStyle} alt={t('login.logoAlt')} />
						</a>
					)}
					{cardBody(false)}
				</div>

				<Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}>
					<Alert onClose={handleCloseSnackbar} severity={updateType} sx={{width: '100%'}}>
						{updateMessage}
					</Alert>
				</Snackbar>
			</div>

			{/* Outside .login-root: the animation scales and shakes the root, and a
			    fixed element inside a transformed parent would move with it. */}
			<canvas className="login-launch" ref={launchCanvasRef} />
		</>
	);
}

export default LoginApp;
